import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import ChatWidget from '@/components/chat/ChatWidget';
import { Hotel, MessageSquare, Settings, Code, Globe, CreditCard, Shield, Clock, Zap, Redo, ArrowDown } from 'lucide-react';

const LandingPage = () => {
  // Flag to control section visibility (set to false to hide sections)
  const SHOW_EXTRA_SECTIONS = false;

  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://lobier.com/embed/lobier-chat.js";
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);
  return (
    <MainLayout>
      {/* Hero Section */}
      <section className="bg-black text-white py-10 md:py-20 relative">
        <img 
              src="/images/lobier-bg2.webp" 
              alt="Lobier AI Chat for Hotels" 
              className="absolute top-0 left-0 w-full h-full object-cover z-0"
            />
        <div className="container mx-auto md:px-4 px-0 relative z-10">
          <div className="grid grid-cols-1 gap-0 md:gap-4 items-center">
            <div className="w-auto h-full flex flex-col justify-center animate-slide-up text-center relative ">
              
              <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-8">
              AI Concierge for Hotels
              </h1>
              <p className="text-sm md:text-xl mb-20 md:mb-[10rem] text-gray-100">
              Answer guest questions. Automatically. Instantly.
              </p>
              <h3 className='text-sm md:text-lg mb-8 md:mb-20 text-gray-100 hidden'>Speaks 4+ Languages</h3>

            </div>
            <div  className="relative flex flex-col justify-start items-start rounded-xl md:p-4 gap-6 animate-fade-in mx-auto w-80 md:w-[400px]">
              <div className='flex justify-start items-center green-shadow text-sm text-white bg-black rounded-lg rounded-bl-none w-[270px] py-3 px-4 animate-float'>
                Welcome! How can I help you today?
              </div>
              <div className='w-full flex justify-end'>
                <div className='flex justify-center items-center green-shadow text-sm text-white bg-[#03A05C] rounded-xl rounded-br-none py-3 px-4 animate-float-2'>
                Hi, do you offer airport transfer?
                </div>
              </div>
              <div className='flex justify-center items-center green-shadow text-sm text-white bg-black rounded-xl rounded-bl-none w-[270px] py-3 px-4 animate-float-3'>
              Yes, we offer airport shuttle service
              for €50 per person.
              </div>
              <div className='w-full flex justify-end'>
                <img src="/images/send.svg" alt="Lobier AI Chat Widget Demo" className="green-shadow animate-float-2" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-20 bg-black">
        <div className="container mx-auto md:px-20 my-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20 max-w-4xl mx-auto mb-16">
          
            <div className="relative flex justify-end hidden">
              <img 
                src="/images/lobier-ai-chat.png" 
                alt="Lobier AI Chat Widget Demo" 
                className="rounded-lg shadow-2xl max-w-sm w-full"
              />
            </div>

              <div className="p-6 flex justify-end w-full">
                <div className="w-80">
                  <div className="bg-hotel-secondary/10 rounded-full p-3 inline-block mb-4">
                    <MessageSquare className="h-6 w-6 text-hotel-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-slate-200 ">AI-powered Responses</h3>
                  <p className="text-slate-400">
                    Intelligent conversations using AI for natural, helpful answers to guest questions.
                  </p>
                </div>
              </div>
              <div className="p-6 flex justify-start w-full">
                <div className="w-80">
                  <div className="bg-hotel-secondary/10 rounded-full p-3 inline-block mb-4">
                    <Zap className="h-6 w-6 text-hotel-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-slate-200">Easy Setup</h3>
                  <p className="text-slate-400">
                    Upload your hotel information, FAQs, or FactSheet. That's it!
                  </p>
                </div>
              </div>
              <div className="p-6 flex justify-end w-full">
                <div className="w-80">
                  <div className="bg-hotel-secondary/10 rounded-full p-3 inline-block mb-4">
                    <Globe className="h-6 w-6 text-hotel-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-slate-200">Multilingual AI Concierge</h3>
                  <p className="text-slate-400">
                  Communicate seamlessly with guests in English, Turkish, German, Russian and more.
                  </p>
                </div>
              </div>
              <div className="p-6 flex justify-start w-full">
                <div className="w-80">
                  <div className="bg-hotel-secondary/10 rounded-full p-3 inline-block mb-4">
                    <Code className="h-6 w-6 text-hotel-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-slate-200">One-Line Install</h3>
                  <p className="text-slate-400">
                    Just copy and paste a single line of code to add the chat widget to your hotel website.
                  </p>
                </div>
              </div>

          </div>
          
          
        </div>
        
        {/* Single-line CTA */}
        <div className="text-center py-20 px-10 bg-gradient-to-b from-black to-black border-t border-slate-800 mt-20">
         <h2 className="text-2xl  mb-10 mt-10 text-slate-200">Ready to see how it works on your website?</h2>
          <Link to="/contact" className="text-white bg-green-700 px-5 py-3 rounded-lg hover:bg-green-800 transition-colors text-md">
              Request Free Demo →
          </Link>
          <div className='py-10'></div>
        </div>
        
        {/* Demo Video Section */}
        <div className="text-center bg-gradient-to-b from-slate-900 via-slate-900 to-black py-20">
          <h3 className="text-lg mb-8 text-slate-200">Watch a 40-second demo</h3>
          <div className="max-w-4xl mx-5 md:mx-auto rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-900 hover:-translate-y-2">
            <video 
              className="w-full rounded-lg shadow-lg" 
              controls
              poster="/images/poster.webp"
            >
              <source src="/images/lobier-video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
        
        <div className='flex md:items-end justify-start md:justify-end text-white gap-2 fixed bottom-10 end-20'>
          Try it Live
          <Redo className="h-6 w-6 text-white mb-1" />
        </div>
      </section>

      {/* How It Works Section */}
      {SHOW_EXTRA_SECTIONS && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-hotel-primary">How It Works</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Get up and running in three simple steps
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 h-full w-0.5 bg-hotel-secondary/30 transform md:translate-x-[-0.5px]" />
                
                <div className="space-y-12">
                  {/* Step 1 */}
                  <div className="relative grid grid-cols-1 md:grid-cols-5 gap-x-8 items-center">
                    <div className="col-span-2 order-2 md:order-1 pt-8 md:pt-0">
                      <h3 className="text-xl font-semibold mb-2 text-hotel-primary">Sign up & Add Hotel Info</h3>
                      <p className="text-gray-600">
                        Create an account and input your hotel details, services, and frequently asked questions.
                      </p>
                    </div>
                    
                    <div className="md:col-span-1 flex justify-center order-1 md:order-2">
                      <div className="w-8 h-8 rounded-full bg-hotel-secondary text-white flex items-center justify-center relative z-10">
                        1
                      </div>
                    </div>
                    
                    <div className="col-span-2 order-3">
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <Hotel className="h-12 w-12 text-hotel-secondary mb-2" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 2 */}
                  <div className="relative grid grid-cols-1 md:grid-cols-5 gap-x-8 items-center">
                    <div className="col-span-2 order-2 md:order-3 pt-8 md:pt-0">
                      <h3 className="text-xl font-semibold mb-2 text-hotel-primary">Customize Your AI</h3>
                      <p className="text-gray-600">
                        Tailor the appearance and behavior of your chat widget to match your hotel's brand.
                      </p>
                    </div>
                    
                    <div className="md:col-span-1 flex justify-center order-1 md:order-2">
                      <div className="w-8 h-8 rounded-full bg-hotel-secondary text-white flex items-center justify-center relative z-10">
                        2
                      </div>
                    </div>
                    
                    <div className="col-span-2 order-3 md:order-1">
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <Settings className="h-12 w-12 text-hotel-secondary mb-2" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 3 */}
                  <div className="relative grid grid-cols-1 md:grid-cols-5 gap-x-8 items-center">
                    <div className="col-span-2 order-2 md:order-1 pt-8 md:pt-0">
                      <h3 className="text-xl font-semibold mb-2 text-hotel-primary">Add to Your Website</h3>
                      <p className="text-gray-600">
                        Copy the embed code and paste it into your website. Your AI concierge is now live!
                      </p>
                    </div>
                    
                    <div className="md:col-span-1 flex justify-center order-1 md:order-2">
                      <div className="w-8 h-8 rounded-full bg-hotel-secondary text-white flex items-center justify-center relative z-10">
                        3
                      </div>
                    </div>
                    
                    <div className="col-span-2 order-3">
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <Code className="h-12 w-12 text-hotel-secondary mb-2" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {SHOW_EXTRA_SECTIONS && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-hotel-primary">Why Choose Lobier AI</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Benefits that go beyond typical customer service
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <Globe className="h-8 w-8 text-hotel-accent mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-hotel-primary">24/7 Availability</h3>
                <p className="text-gray-600">
                  Always there to assist your guests, no matter the time of day.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <CreditCard className="h-8 w-8 text-hotel-accent mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-hotel-primary">Increase Bookings</h3>
                <p className="text-gray-600">
                  Convert more website visitors into guests with instant responses.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <Shield className="h-8 w-8 text-hotel-accent mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-hotel-primary">Cost Effective</h3>
                <p className="text-gray-600">
                  More affordable than hiring additional staff for customer service.
                </p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <Clock className="h-8 w-8 text-hotel-accent mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-hotel-primary">Save Time</h3>
                <p className="text-gray-600">
                  Reduce repetitive questions to your front desk and reservation teams.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
      
      <div 
        id="LobierChatWidget"
        data-id="6ae906a2-57ad-4d08-9a54-1edeab4af5fb"
        data-position="bottom-right"
        data-primary-color="#013896"
        data-secondary-color="#4299E1"
      ></div>

    </MainLayout>
  );
};

export default LandingPage;
